/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type DeleteOAuthClientMutationConfig = {
  onSuccess?: (data: void, variables: string) => void;
  onError?: (error: unknown, variables: string) => void;
  onSettled?: (data: unknown, error: unknown, variables: unknown) => void;
  onMutate?: (variables: string) => Promise<unknown> | void;
};

/**
 * Hook for deleting an OAuth client by ID through the management plane API.
 *
 * @param config.onSuccess - Called after successful OAuth client deletion
 * @param config.onError - Called if deletion fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 * @param config.onMutate - Called before mutation executes
 *
 * @returns React Query mutation result for deleting OAuth clients
 *
 * @example
 * ```tsx
 * const { mutate } = useDeleteOAuthClientMutation({
 *   onSuccess: () => console.log('OAuth client deleted'),
 *   onError: (error) => console.error('Deletion failed:', error)
 * });
 *
 * // Delete OAuth client
 * mutate(oauthClientId);
 * ```
 */
export function useDeleteOAuthClientMutation({
  onSuccess,
  onError,
  onSettled,
  onMutate,
}: DeleteOAuthClientMutationConfig) {
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const queryClient = useQueryClient();

  const { mutate, isError, error, isPending } = useMutation<void, ApiError, string>({
    mutationFn: async (oauthClientId: string) => {
      await mgmtPlaneAPIClient.oauthClients.deleteOauthClient({
        tenantId: tenantId!,
        oauthClientId,
      });
    },
    onSuccess: (data, variables) => {
      // Manually invalidate the OAuth clients query cache
      queryClient.invalidateQueries({ queryKey: ['oauthClients'] });

      // Call the user-provided onSuccess callback
      onSuccess?.(data, variables);
    },
    onError,
    onSettled,
    onMutate,
  });

  return { mutate, isError, error, isPending };
}
